import { motion, useReducedMotion, type Variants } from "motion/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { createStaggerContainer, createStaggerItem, REVEAL_AMOUNT, REVEAL_EASE } from "@/src/common/components/animated-section";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { SectionHeading } from "@/src/common/components/section-heading";
import { type EntranceVariant, toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";

const STEPS = ["1", "2", "3"] as const;

function createLineVariants(reduced: boolean): Variants {
	return {
		hidden: { pathLength: reduced ? 1 : 0, transition: { duration: 0 } },
		visible: { pathLength: 1, transition: reduced ? { duration: 0 } : { duration: 1.4, ease: REVEAL_EASE, delay: 0.2 } },
	};
}

function createBadgeVariants(reduced: boolean): Variants {
	return {
		hidden: { scale: reduced ? 1 : 0, transition: { duration: 0 } },
		visible: { scale: 1, transition: reduced ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 16, delay: 0.25 } },
	};
}

function ConnectorLine({ reduced, entrance }: { reduced: boolean; entrance: EntranceVariant }) {
	const variants = createLineVariants(reduced);
	return (
		<>
			<svg
				aria-hidden="true"
				className="pointer-events-none absolute left-[46px] right-[calc(33.333%-46px)] top-[2px] z-10 hidden h-0.5 md:block"
				viewBox="0 0 100 2"
				preserveAspectRatio="none"
			>
				<motion.path
					d="M0 1 H100"
					fill="none"
					stroke="oklch(0.6204 0.195 253.83 / 0.5)"
					strokeWidth="2"
					vectorEffect="non-scaling-stroke"
					initial={false}
					animate={entrance}
					variants={variants}
				/>
			</svg>
			<svg
				aria-hidden="true"
				className="pointer-events-none absolute bottom-24 left-[45px] top-[2px] z-10 w-0.5 md:hidden"
				viewBox="0 0 2 100"
				preserveAspectRatio="none"
			>
				<motion.path
					d="M1 0 V100"
					fill="none"
					stroke="oklch(0.6204 0.195 253.83 / 0.5)"
					strokeWidth="2"
					vectorEffect="non-scaling-stroke"
					initial={false}
					animate={entrance}
					variants={variants}
				/>
			</svg>
		</>
	);
}

export function GettingStartedSection() {
	const { t } = useTranslation("home");
	const reduced = useReducedMotion() === true;
	const listRef = useRef<HTMLOListElement>(null);
	const entrance = toEntranceVariant(useEntrance(listRef, { amount: REVEAL_AMOUNT, disabled: reduced }));
	const container = createStaggerContainer(reduced, 0.14, 0.1);
	const item = createStaggerItem(reduced, 24);
	const badge = createBadgeVariants(reduced);

	return (
		<section className="border-y border-default-200/60 bg-content1/40 py-24 md:py-32">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<SectionHeading
					eyebrow={<EyebrowBadge tone="ink">{t("getting_started.eyebrow")}</EyebrowBadge>}
					title={t("getting_started.title")}
					titleAccent={t("getting_started.title_accent")}
				/>

				<div className="relative mt-16">
					<ConnectorLine reduced={reduced} entrance={entrance} />
					<motion.ol
						ref={listRef}
						className="relative grid gap-8 md:grid-cols-3 md:gap-6"
						initial={false}
						animate={entrance}
						variants={container}
					>
						{STEPS.map((idx) => (
							<motion.li
								key={idx}
								variants={item}
								className="relative flex flex-col gap-4 rounded-3xl border border-default-200 bg-white p-7 shadow-card hover-lift"
							>
								<motion.span
									className="absolute -top-4 left-7 z-20 inline-flex size-9 items-center justify-center rounded-2xl bg-brand-gradient text-sm font-bold text-white shadow-lg shadow-brand-700/30"
									variants={badge}
								>
									{idx}
								</motion.span>
								<h3 className="mt-4 text-lg font-bold text-foreground">{t(`getting_started.step_${idx}.title`)}</h3>
								<p className="text-sm text-default-600">{t(`getting_started.step_${idx}.body`)}</p>
							</motion.li>
						))}
					</motion.ol>
				</div>
			</div>
		</section>
	);
}
